/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import Styles from '../../css/LoanInformation.module.css';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import {
  formatZipCode,
  getFormattedPhoneExt,
  getFormattedPhoneNumber,
  isObjEmptyOrNull,
  isStrEmptyOrNull,
  buildFullName,
} from '../../common/UtilityFunctions';
import * as zipcodeToTimezone from 'zipcode-to-timezone';
import { PatternFormat } from 'react-number-format';
import { Fragment, useCallback, useMemo, useState } from 'react';
import useCollectionStatusList from '../../common/hooks/useCollectionStatusList';
import useQidList from '../../common/hooks/useQidList';
import useUserSearch from '../../common/hooks/useUserSearch';

const ContactInfo = () => {
  const {
    loanData,
    handleLoanChange,
    servicingData,
    handleServicingChange,
    propData,
    handlePropChange,
    lossMitData,
    loanContacts,
    handleLossMitiChange,
    borrowersArr,
    handleLoanBorrowersChange,
    loanBorrowers,
    bttcanytime,
    handleAnytimeChange,
  } = useLoanInformationContext();
  const [borrowersToNotRender, setBorrowersToNotRender] = useState([]);
  const currentPrimaryNumber = (loanData?.primaryBorrowerNumber ?? loanData?.primaryBorrowerNumber) || null;

  const toggleBrwr = useCallback(
    (borrowerNumber: string) => {
      if (borrowersToNotRender.includes(borrowerNumber)) {
        //currently shown -> minimized
        let newRenders = borrowersToNotRender.filter((brwrNum) => brwrNum !== borrowerNumber);
        setBorrowersToNotRender(newRenders);
      } else {
        //currently minimized -> shown
        setBorrowersToNotRender((borrowers) => {
          return [...borrowers, borrowerNumber];
        });
      }
    },
    [borrowersToNotRender],
  );
  const { qidCollectData, qidCollectLoaded } = useCollectionStatusList();
  const { qid: preferredLanguageQID } = useQidList('BRW1_CD.319');
  const { qid: phoneStatusQID } = useQidList('BRW1_CD.014');

  const currentRepId = (lossMitData?.representative || '').trim();
  //for delete button(s) on related_phone_array entries
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const buildRepLabel = (u: RepUser) => {
    const full = [u?.nameFirst, u?.nameMiddle, u?.nameLast]
      .map((s) => (s == null ? '' : String(s).trim()))
      .filter(Boolean)
      .join(' ');
    return [full, u?.ID].filter(Boolean).join(' - ');
  };

  const findRepById = (arr: RepUser[], id?: string | null) =>
    arr.find((u) => (u?.ID || '').trim() === (id || '').trim());

  const { qid: dayToContactQID } = useQidList('BRW1_CD.E01');
  const { qid: beforeAfterQID } = useQidList('BRW1_CD.E02');
  const { qid: amPmQID } = useQidList('BRW1_CD.E04');
  const { qid: timezoneQID } = useQidList('BRW1_CD.E05');

  //Pulling mtg assist rep name using representative field
  const { users: repoptions, loadingUsers } = useUserSearch({
    returnFields: ['nameFirst', 'nameMiddle', 'nameLast', 'ID'],
    duty: 'LSR',
  });

  const repdata = findRepById(repoptions, currentRepId);
  const { users: repdatasearchoptions } = useUserSearch({
    ID: currentRepId,
    returnFields: ['nameFirst', 'nameMiddle', 'nameLast'],
  });
  const repdatabackup = repdatasearchoptions.length > 0 ? repdatasearchoptions[0] : null;

  const isBTTCEmpty = useCallback((bttc: BTTCObj) => {
    if (isObjEmptyOrNull(bttc)) return true;
    for (const property in bttc) {
      if (property !== '$index' && !isStrEmptyOrNull(bttc[property])) {
        return false;
      }
    }
    return true;
  }, []);

  const bttcOneAnyTimeChecked = useMemo(() => {
    if (bttcanytime[0].changed) return bttcanytime[0].anytime;
    return isBTTCEmpty(
      loanBorrowers[currentPrimaryNumber]?.bttc_array?.filter((i) => i.$index == 0)[0]
        ? loanBorrowers[currentPrimaryNumber]?.bttc_array?.filter((i) => i.$index == 0)[0]
        : null,
    );
  }, [loanBorrowers[currentPrimaryNumber], bttcanytime]);

  const bttcTwoAnyTimeChecked = useMemo(() => {
    if (bttcanytime[1].changed) return bttcanytime[1].anytime;
    return isBTTCEmpty(
      loanBorrowers[currentPrimaryNumber]?.bttc_array?.filter((i) => i.$index == 1)[0]
        ? loanBorrowers[currentPrimaryNumber]?.bttc_array?.filter((i) => i.$index == 1)[0]
        : null,
    );
  }, [loanBorrowers[currentPrimaryNumber], bttcanytime]);

  const clearBttc = useCallback(
    (borrowerNo: string, bttcIndex: number) => {
      const currBttc = !isObjEmptyOrNull(loanBorrowers[borrowerNo]?.bttc_array)
        ? loanBorrowers[borrowerNo]?.bttc_array
        : [];

      var bttcAtIndex: BTTCObj = { $index: bttcIndex };
      bttcAtIndex['dayToContactKeyword'] = '';
      bttcAtIndex['beforeAfterKeyword'] = '';
      bttcAtIndex['timeToContact'] = '';
      bttcAtIndex['amPmKeyword'] = '';
      bttcAtIndex['timeZoneKeyword'] = '';
      var newBttcArr = currBttc.filter((bttc) => bttc.$index !== bttcIndex);

      newBttcArr.push(bttcAtIndex);

      handleLoanBorrowersChange('edit', borrowerNo + '.bttc_array', newBttcArr);
    },
    [loanBorrowers, handleLoanBorrowersChange],
  );

  const handleBTTCChange = useCallback(
    (borrowerNo: string, bttcIndex: number, variable: string, val: string) => {
      const currBttc = !isObjEmptyOrNull(loanBorrowers[borrowerNo]?.bttc_array)
        ? loanBorrowers[borrowerNo]?.bttc_array
        : [];

      var bttcAtIndex: BTTCObj = !isObjEmptyOrNull(currBttc.filter((bttc) => bttc.$index == bttcIndex)[0])
        ? { ...currBttc.filter((bttc) => bttc.$index == bttcIndex)[0] }
        : { $index: bttcIndex };

      delete bttcAtIndex.amPm;
      delete bttcAtIndex.amPmDescription;
      delete bttcAtIndex.beforeAfter;
      delete bttcAtIndex.beforeAfterDescription;
      delete bttcAtIndex.dayToContact;
      delete bttcAtIndex.dayToContactDescription;
      delete bttcAtIndex.timeZone;
      delete bttcAtIndex.timeZoneDescription;
      if (isObjEmptyOrNull(bttcAtIndex)) {
        bttcAtIndex[variable] = val;
      } else {
        bttcAtIndex[variable] = val;
      }

      var newBttcArr = currBttc.filter((bttc) => bttc.$index !== bttcIndex);

      newBttcArr.push(bttcAtIndex);

      handleLoanBorrowersChange('edit', borrowerNo + '.bttc_array', newBttcArr);
    },
    [loanBorrowers, handleLoanBorrowersChange],
  );

  const deleteRelatedPhone = useCallback(
    (borrowerNo: string, index: number) => {
      const currPhoneArr = !isObjEmptyOrNull(loanBorrowers[borrowerNo]?.related_phone_array)
        ? loanBorrowers[borrowerNo]?.related_phone_array
        : [];
      var phoneAtIndex = {
        $index: index,
        relatedPhoneNumber: '',
        relatedPhoneTag: '',
      };
      var newPhoneArr = currPhoneArr.filter((phone) => phone.$index !== index);
      newPhoneArr.push(phoneAtIndex);
      handleLoanBorrowersChange('edit', borrowerNo + '.related_phone_array', newPhoneArr);
    },
    [loanBorrowers, handleLoanBorrowersChange],
  );

  const handleRepChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newId = e.target.value.trim();
      handleLossMitiChange('edit', 'representative', newId);
    },
    [handleLossMitiChange],
  );
  return (
    <EnvelopeWithBox blockName='Contact Information'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Security PIN:</th>
                <td>
                  <input
                    type='text'
                    name='securityPin'
                    value={servicingData?.securityPin || ''}
                    onChange={(e) => {
                      const newValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                      handleServicingChange('edit', 'securityPin', newValue);
                    }}
                    placeholder='Set PIN (4 digits)'
                  />
                </td>
              </tr>
              <tr>
                {/*This field only accepts 2 characters in backend, so I associated numbers with each question for saving purposes.*/}
                <th>Security Question:</th>
                <td>
                  <select
                    name='securityQuestion'
                    value={servicingData?.securityQuestion || ''}
                    onChange={(e) => handleServicingChange('edit', 'securityQuestion', e.target.value)}
                  >
                    <option value=''>Select a question</option>
                    <option value='01'>01 - What is your favorite pet's name?</option>
                    <option value='02'>02 - What is your mother's middle name?</option>
                    <option value='03'>03 - What is your father's middle name?</option>
                    <option value='04'>04 - What is your favorite sport?</option>
                    <option value='05'>05 - What is the street no. of the house you grew up in?</option>
                    <option value='06'>06 - What high school did you attend?</option>
                    <option value='07'>07 - What city were you born in?</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Security Answer:</th>
                <td>
                  <input
                    type='text'
                    name='securityAnswer'
                    value={servicingData?.securityAnswer || ''}
                    onChange={(e) => handleServicingChange('edit', 'securityAnswer', e.target.value)}
                    placeholder='Answer'
                  />
                </td>
              </tr>
              <tr>
                <th>Property Address:</th>
                <td>
                  <input
                    type='text'
                    name='address'
                    value={propData?.address || ''}
                    onChange={(e) => handlePropChange('edit', 'address', e.target.value)}
                    placeholder='Property Address'
                  />
                </td>
              </tr>
              <tr>
                <th>Property Unit Number:</th>
                <td>
                  <input
                    type='text'
                    name='unitNumber'
                    value={propData?.unitNumber || ''}
                    onChange={(e) => handlePropChange('edit', 'unitNumber', e.target.value)}
                    placeholder='Property Unit Number'
                  />
                </td>
              </tr>
              <tr>
                <th>Property City/State/Zip:</th>
                <td>
                  <input
                    type='text'
                    name='city'
                    value={propData?.city}
                    onChange={(e) => handlePropChange('edit', 'city', e.target.value)}
                    placeholder='City'
                  />
                  <input
                    type='text'
                    name='state'
                    value={propData?.state}
                    onChange={(e) => handlePropChange('edit', 'state', e.target.value)}
                    placeholder='State'
                  />
                  <input
                    type='text'
                    name='zipCode'
                    value={formatZipCode(propData?.zipCode)}
                    onChange={(e) => {
                      const onlyDigits = e.target.value.replace(/\D/g, '');
                      handlePropChange('edit', 'zipCode', onlyDigits);
                    }}
                    placeholder='Zip Code'
                  />
                </td>
              </tr>
              <tr>
                <th>Mailing Address:</th>
                <tr className={Styles.mailingrow}>
                  <th>Same as property address:</th>
                  <td>
                    <input
                      type='checkbox'
                      name='sameasproperty'
                      className={Styles.checkboxAnyTime}
                      checked={loanData?.defaultMailingAddress}
                      onChange={(e) => {
                        handleLoanChange('edit', 'defaultMailingAddress', e.target.checked);
                      }}
                    ></input>
                  </td>
                  <td>
                    <input
                      type='text'
                      name='mailingAddress'
                      disabled={loanData?.defaultMailingAddress}
                      value={loanData?.mailingAddress || ''}
                      onChange={(e) => handleLoanChange('edit', 'mailingAddress', e.target.value)}
                      placeholder='Mailing Address'
                    />
                  </td>
                </tr>
              </tr>
              <tr>
                <th>Mailing Unit Number:</th>
                <tr className={Styles.mailingrow}>
                  <th>Same as property unit:</th>
                  <td>
                    <input
                      type='checkbox'
                      name='sameaspropertyunit'
                      className={Styles.checkboxAnyTime}
                      checked={loanData?.defaultMailingUnitNumber}
                      onChange={(e) => {
                        handleLoanChange('edit', 'defaultMailingUnitNumber', e.target.checked);
                      }}
                    ></input>
                  </td>
                  <td>
                    <input
                      type='text'
                      name='mailingUnitNumber'
                      disabled={loanData?.defaultMailingUnitNumber}
                      value={loanData?.mailingUnitNumber || ''}
                      onChange={(e) => handleLoanChange('edit', 'mailingUnitNumber', e.target.value)}
                      placeholder='Mailing Unit Number'
                    />
                  </td>
                </tr>
              </tr>
              <tr>
                <th>Mailing City/State/Zip:</th>
                <tr className={Styles.mailingcitystatezip}>
                  <tr className={Styles.mailingrow}>
                    <th>Same as property city:</th>
                    <td>
                      <input
                        type='checkbox'
                        name='sameaspropertycity'
                        className={Styles.checkboxAnyTime}
                        checked={loanData?.defaultMailingCity}
                        onChange={(e) => {
                          handleLoanChange('edit', 'defaultMailingCity', e.target.checked);
                        }}
                      ></input>
                    </td>
                    <td>
                      <input
                        type='text'
                        name='mailingCity'
                        value={loanData?.mailingCity}
                        disabled={loanData?.defaultMailingCity}
                        onChange={(e) => handleLoanChange('edit', 'mailingCity', e.target.value)}
                        placeholder='City'
                      />
                    </td>
                  </tr>

                  <tr className={Styles.mailingrow}>
                    <th>Same as property state:</th>
                    <td>
                      <input
                        type='checkbox'
                        name='sameaspropertystate'
                        className={Styles.checkboxAnyTime}
                        checked={loanData?.defaultMailingState}
                        onChange={(e) => {
                          handleLoanChange('edit', 'defaultMailingState', e.target.checked);
                        }}
                      ></input>
                    </td>
                    <td>
                      <input
                        type='text'
                        name='mailingState'
                        value={loanData?.mailingState}
                        disabled={loanData?.defaultMailingState}
                        onChange={(e) => handleLoanChange('edit', 'mailingState', e.target.value)}
                        placeholder='State'
                      />
                    </td>
                  </tr>

                  <tr className={Styles.mailingrow}>
                    <th>Same as property zip code:</th>
                    <td>
                      <input
                        type='checkbox'
                        name='sameaspropertyzip'
                        className={Styles.checkboxAnyTime}
                        checked={loanData?.defaultMailingZipCode}
                        onChange={(e) => {
                          handleLoanChange('edit', 'defaultMailingZipCode', e.target.checked);
                        }}
                      ></input>
                    </td>
                    <td>
                      <input
                        type='text'
                        name='mailingZipCode'
                        value={formatZipCode(loanData?.mailingZipCode)}
                        disabled={loanData?.defaultMailingZipCode}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, '');
                          handleLoanChange('edit', 'mailingZipCode', onlyDigits);
                        }}
                        placeholder='Zip Code'
                      />
                    </td>
                  </tr>
                </tr>
              </tr>
              <tr>
                <th>Estimated Borrower Timezone:</th>
                <td>{loanData?.mailingZipCode ? zipcodeToTimezone.lookup(loanData.mailingZipCode.slice(0, 5)) : ''}</td>
              </tr>
              {borrowersArr
                .filter((borrower) => borrower?.borrowerOnNote && borrower?.borrowerTypeKeyword !== 'REMOVED')
                .map((borrower, idx) => (
                  <Fragment key={borrower?.borrowerNumber}>
                    <tr className={Styles.separatorCellData}>
                      <th className={Styles.separatorCellHeader} onClick={() => toggleBrwr(borrower?.borrowerNumber)}>
                        <h4>
                          Borrower {borrower?.borrowerNumber}
                          {borrower.borrowerNumber === currentPrimaryNumber ? ' - Primary' : ''}:
                        </h4>
                      </th>
                      <td>{borrower?.fullName}</td>
                    </tr>
                    {!borrowersToNotRender.includes(borrower?.borrowerNumber) && (
                      <>
                        <tr>
                          <th>Preferred Language:</th>
                          <td>
                            {preferredLanguageQID.length == 0 ? (
                              <span>Loading</span>
                            ) : (
                              <select
                                name={`preferredLanguageDescription-${borrower.borrowerNumber}`}
                                value={
                                  isStrEmptyOrNull(borrower.preferredLanguage) ? 'disabled' : borrower.preferredLanguage
                                }
                                onChange={(e) =>
                                  handleLoanBorrowersChange(
                                    'edit',
                                    borrower.borrowerNumber + '.preferredLanguage',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value='disabled' disabled>
                                  Select Preferred Language
                                </option>
                                {preferredLanguageQID.map((opt, j) => (
                                  <option key={j} value={opt.code}>
                                    {opt.description}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th>Home Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`homePhoneNumber-${borrower?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(borrower?.homePhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower?.borrowerNumber + '.homePhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`Borrower ${borrower?.borrowerNumber} Home Phone`}
                              className={Styles.firstInputRow}
                            />
                            <select
                              name={`homePhoneBorrowerApproved-${borrower.borrowerNumber}`}
                              value={borrower?.homePhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.homePhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.secondInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`homePhoneNumberStatusQID-${borrower.borrowerNumber}`}
                              value={borrower?.homePhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.homePhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <th>Mobile Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`mobilePhoneNumber-${borrower?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(borrower?.mobilePhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower?.borrowerNumber + '.mobilePhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`Borrower ${borrower?.borrowerNumber} Mobile Phone`}
                              className={Styles.firstInputRow}
                            />
                            <select
                              name={`mobilePhoneBorrowerApproved-${borrower.borrowerNumber}`}
                              value={borrower?.mobilePhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.mobilePhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.secondInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`mobilePhoneNumberStatusQID-${borrower.borrowerNumber}`}
                              value={borrower?.mobilePhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.mobilePhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <th>Work Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`workPhoneNumber-${borrower?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(borrower?.workPhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower?.borrowerNumber + '.workPhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`Borrower ${borrower?.borrowerNumber} Work Phone`}
                              className={Styles.workPhoneInputRow}
                            />
                            <input
                              type='text'
                              name={`workPhoneNumberExt-${borrower?.borrowerNumber}`}
                              value={getFormattedPhoneExt(borrower?.workExt || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange('edit', borrower?.borrowerNumber + '.workExt', cleanedValue);
                              }}
                              placeholder={`Work Ext.`}
                              className={Styles.workExtInputRow}
                            />
                            <select
                              name={`workPhoneBorrowerApproved-${borrower.borrowerNumber}`}
                              value={borrower?.workPhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.workPhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.workConsentInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`workPhoneNumberStatusQID-${borrower.borrowerNumber}`}
                              value={borrower?.workPhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  borrower.borrowerNumber + '.workPhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        {borrower.borrowerNumber === loanData?.primaryBorrowerNumber && (
                          <>
                            <tr className={Styles.bttcarray}>
                              <th>BTTC 1:</th>
                              <tr>
                                <th>Any Time:</th>
                                <td>
                                  <input
                                    type='checkbox'
                                    className={Styles.checkboxAnyTime}
                                    checked={bttcOneAnyTimeChecked}
                                    onChange={(e) => {
                                      handleAnytimeChange('edit', '0.changed', true);
                                      handleAnytimeChange('edit', '0.anytime', e.target.checked);
                                      if (e.target.checked == true) {
                                        clearBttc(borrower.borrowerNumber, 0);
                                      }
                                    }}
                                  />
                                </td>
                              </tr>

                              {!bttcOneAnyTimeChecked && (
                                <tr>
                                  <th>Day to Contact:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 0)[0]?.dayToContactKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(
                                          borrower.borrowerNumber,
                                          0,
                                          'dayToContactKeyword',
                                          e.target.value,
                                        );
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {dayToContactQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcOneAnyTimeChecked && (
                                <tr>
                                  <th>Before/After:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 0)[0]?.beforeAfterKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(
                                          borrower.borrowerNumber,
                                          0,
                                          'beforeAfterKeyword',
                                          e.target.value,
                                        );
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {beforeAfterQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcOneAnyTimeChecked && (
                                <tr>
                                  <th>Time to Contact:</th>
                                  <td>
                                    <PatternFormat
                                      format={'##:##'}
                                      value={borrower?.bttc_array?.filter((i) => i.$index == 0)[0]?.timeToContact || ''}
                                      onChange={(e) => {
                                        const hourminarr = e.target.value.split(':');
                                        const hour = parseInt(hourminarr[0]);
                                        const minute = parseInt(hourminarr[1]);
                                        if (!isNaN(hour) && !isNaN(minute)) {
                                          if (hour > 12) {
                                            e.target.value = '';
                                          }
                                          if (minute > 60) {
                                            e.target.value = '';
                                          }
                                        }

                                        handleBTTCChange(borrower.borrowerNumber, 0, 'timeToContact', e.target.value);
                                      }}
                                      placeholder='HH:MM (e.g., 12:30)'
                                    />
                                  </td>
                                </tr>
                              )}
                              {!bttcOneAnyTimeChecked && (
                                <tr>
                                  <th>AM/PM:</th>
                                  <td>
                                    <select
                                      value={borrower?.bttc_array?.filter((i) => i.$index == 0)[0]?.amPmKeyword || ''}
                                      onChange={(e) => {
                                        handleBTTCChange(borrower.borrowerNumber, 0, 'amPmKeyword', e.target.value);
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {amPmQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcOneAnyTimeChecked && (
                                <tr>
                                  <th>Time Zone:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 0)[0]?.timeZoneKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(borrower.borrowerNumber, 0, 'timeZoneKeyword', e.target.value);
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {timezoneQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                            </tr>
                            <tr className={Styles.bttcarray}>
                              <th>BTTC 2:</th>
                              <tr>
                                <th>Any Time:</th>
                                <td>
                                  <input
                                    type='checkbox'
                                    className={Styles.checkboxAnyTime}
                                    checked={bttcTwoAnyTimeChecked}
                                    onChange={(e) => {
                                      handleAnytimeChange('edit', '1.changed', true);
                                      handleAnytimeChange('edit', '1.anytime', e.target.checked);
                                      if (e.target.checked == true) {
                                        clearBttc(borrower.borrowerNumber, 1);
                                      }
                                    }}
                                  />
                                </td>
                              </tr>
                              {!bttcTwoAnyTimeChecked && (
                                <tr>
                                  <th>Day to Contact:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 1)[0]?.dayToContactKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(
                                          borrower.borrowerNumber,
                                          1,
                                          'dayToContactKeyword',
                                          e.target.value,
                                        );
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {dayToContactQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcTwoAnyTimeChecked && (
                                <tr>
                                  <th>Before/After:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 1)[0]?.beforeAfterKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(
                                          borrower.borrowerNumber,
                                          1,
                                          'beforeAfterKeyword',
                                          e.target.value,
                                        );
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {beforeAfterQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcTwoAnyTimeChecked && (
                                <tr>
                                  <th>Time to Contact:</th>
                                  <td>
                                    <PatternFormat
                                      format={'##:##'}
                                      value={borrower?.bttc_array?.filter((i) => i.$index == 1)[0]?.timeToContact || ''}
                                      onChange={(e) => {
                                        const hourminarr = e.target.value.split(':');
                                        const hour = parseInt(hourminarr[0]);
                                        const minute = parseInt(hourminarr[1]);
                                        if (!isNaN(hour) && !isNaN(minute)) {
                                          if (hour > 12) {
                                            e.target.value = '';
                                          }
                                          if (minute > 60) {
                                            e.target.value = '';
                                          }
                                        }

                                        handleBTTCChange(borrower.borrowerNumber, 1, 'timeToContact', e.target.value);
                                      }}
                                      placeholder='HH:MM (e.g., 12:30)'
                                    />
                                  </td>
                                </tr>
                              )}
                              {!bttcTwoAnyTimeChecked && (
                                <tr>
                                  <th>AM/PM:</th>
                                  <td>
                                    <select
                                      value={borrower?.bttc_array?.filter((i) => i.$index == 1)[0]?.amPmKeyword || ''}
                                      onChange={(e) =>
                                        handleBTTCChange(borrower.borrowerNumber, 1, 'amPmKeyword', e.target.value)
                                      }
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {amPmQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                              {!bttcTwoAnyTimeChecked && (
                                <tr>
                                  <th>Time Zone:</th>
                                  <td>
                                    <select
                                      value={
                                        borrower?.bttc_array?.filter((i) => i.$index == 1)[0]?.timeZoneKeyword || ''
                                      }
                                      onChange={(e) => {
                                        handleBTTCChange(borrower.borrowerNumber, 1, 'timeZoneKeyword', e.target.value);
                                      }}
                                    >
                                      <option value='' disabled>
                                        Select option
                                      </option>
                                      {timezoneQID.map((c) => (
                                        <option value={c.keyword} key={c.code}>
                                          {c.description}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                </tr>
                              )}
                            </tr>
                          </>
                        )}
                      </>
                    )}
                  </Fragment>
                ))}
              <br />
              {borrowersArr
                .filter((nbp) => !nbp?.borrowerOnNote && nbp?.borrowerTypeKeyword !== 'REMOVED')
                .map((nbp, idx) => (
                  <Fragment key={nbp?.borrowerNumber}>
                    <tr className={Styles.separatorCellData}>
                      <th className={Styles.separatorCellHeader} onClick={() => toggleBrwr(nbp?.borrowerNumber)}>
                        <h4>Non-Borrowing Party {idx + 1}</h4>
                      </th>
                      <td>{nbp?.fullName}</td>
                    </tr>
                    {!borrowersToNotRender.includes(nbp?.borrowerNumber) && (
                      <>
                        <tr>
                          <th>Preferred Language:</th>
                          <td>
                            {preferredLanguageQID.length == 0 ? (
                              <span>Loading</span>
                            ) : (
                              <select
                                name={`preferredLanguageDescription-${nbp.borrowerNumber}`}
                                value={isStrEmptyOrNull(nbp.preferredLanguage) ? 'disabled' : nbp.preferredLanguage}
                                onChange={(e) =>
                                  handleLoanBorrowersChange(
                                    'edit',
                                    nbp.borrowerNumber + '.preferredLanguage',
                                    e.target.value,
                                  )
                                }
                              >
                                <option value='disabled' disabled>
                                  Select Preferred Language
                                </option>
                                {preferredLanguageQID.map((opt, j) => (
                                  <option key={j} value={opt.code}>
                                    {opt.description}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th>Home Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`homePhoneNumber-${nbp?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(nbp?.homePhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp?.borrowerNumber + '.homePhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`nbp ${nbp?.borrowerNumber} Home Phone`}
                              className={Styles.firstInputRow}
                            />
                            <select
                              name={`homePhoneBorrowerApproved-${nbp.borrowerNumber}`}
                              value={nbp?.homePhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.homePhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.secondInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`homePhoneNumberStatusQID-${nbp.borrowerNumber}`}
                              value={nbp?.homePhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.homePhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <th>Mobile Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`mobilePhoneNumber-${nbp?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(nbp?.mobilePhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp?.borrowerNumber + '.mobilePhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`nbp ${nbp?.borrowerNumber} Mobile Phone`}
                              className={Styles.firstInputRow}
                            />
                            <select
                              name={`mobilePhoneBorrowerApproved-${nbp.borrowerNumber}`}
                              value={nbp?.mobilePhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.mobilePhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.secondInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`mobilePhoneNumberStatusQID-${nbp.borrowerNumber}`}
                              value={nbp?.mobilePhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.mobilePhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                        <tr>
                          <th>Work Phone</th>
                          <td>
                            <input
                              type='text'
                              name={`workPhoneNumber-${nbp?.borrowerNumber}`}
                              value={getFormattedPhoneNumber(nbp?.workPhoneNumber || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp?.borrowerNumber + '.workPhoneNumber',
                                  cleanedValue,
                                );
                              }}
                              placeholder={`nbp ${nbp?.borrowerNumber} Work Phone`}
                              className={Styles.workPhoneInputRow}
                            />
                            <input
                              type='text'
                              name={`workPhoneNumberExt-${nbp?.borrowerNumber}`}
                              value={getFormattedPhoneExt(nbp?.workExt || '')}
                              onChange={(e) => {
                                const rawValue = e.target.value || '';
                                const cleanedValue = rawValue.replace(/\D/g, '');
                                handleLoanBorrowersChange('edit', nbp?.borrowerNumber + '.workExt', cleanedValue);
                              }}
                              placeholder={`Work Ext.`}
                              className={Styles.workExtInputRow}
                            />
                            <select
                              name={`workPhoneBorrowerApproved-${nbp.borrowerNumber}`}
                              value={nbp?.workPhoneBorrowerApproved ? 'Consent' : 'No Consent'}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.workPhoneBorrowerApproved',
                                  e.target.value === 'Consent',
                                )
                              }
                              className={Styles.workConsentInputRow}
                            >
                              <option value='Consent'>Consent Available</option>
                              <option value='No Consent'>Consent Not Available</option>
                            </select>
                            <select
                              name={`workPhoneNumberStatusQID-${nbp.borrowerNumber}`}
                              value={nbp?.workPhoneNumberStatus}
                              onChange={(e) =>
                                handleLoanBorrowersChange(
                                  'edit',
                                  nbp.borrowerNumber + '.workPhoneNumberStatus',
                                  e.target.value,
                                )
                              }
                              className={Styles.thirdInputRow}
                              defaultValue=''
                            >
                              <option value=''>Select Phone Status</option>
                              {phoneStatusQID.map((opt, j) => (
                                <option key={j} value={opt.code}>
                                  {opt.description}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                ))}
              <br />
              <tr>
                <th>Queue:</th>
                <td>
                  {servicingData?.servicingQueueDescription && servicingData?.servicingQueueKeyword
                    ? `${servicingData?.servicingQueueKeyword} - ${servicingData?.servicingQueueDescription}`
                    : ''}
                </td>
              </tr>
              <tr>
                <th>Collection Status:</th>
                <td>
                  {!qidCollectLoaded ? (
                    <span>Loading statuses…</span>
                  ) : (
                    <select
                      name='collectionStatus'
                      value={loanData?.collectionStatus != null ? loanData?.collectionStatus : 'disabled'}
                      onChange={(e) => handleLoanChange('edit', 'collectionStatus', e.target.value)}
                    >
                      <option value='disabled' disabled>
                        Select status…
                      </option>
                      <option value=''>None</option>
                      {qidCollectData.map((opt, i) => (
                        <option key={i} value={opt.code}>
                          {opt.code} - {opt.description}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
              <tr>
                <th>Attorney:</th>
                <td>{loanContacts?.AT?.contactPersonFullName ? loanContacts?.AT?.contactPersonFullName : ''}</td>
              </tr>
              <tr>
                <th>Mtg Assist Rep:</th>
                <td>
                  {loadingUsers ? (
                    <span>Loading reps…</span>
                  ) : (
                    <select name='representative' value={currentRepId} onChange={handleRepChange}>
                      <option value=''>Select…</option>

                      {!repoptions.some((u) => (u.ID || '').trim() === currentRepId) && !!currentRepId && (
                        <option value={currentRepId}>
                          {[buildFullName(repdata ? repdata : repdatabackup), currentRepId].filter(Boolean).join(' - ')}
                        </option>
                      )}
                      {repoptions.map((u) => {
                        const id = (u?.ID || '').trim();
                        if (!id) return null;
                        return (
                          <option key={id} value={id}>
                            {buildRepLabel(u)}
                          </option>
                        );
                      })}
                    </select>
                  )}
                </td>
              </tr>
              {borrowersArr.length > 0 && (
                <>
                  {Array.isArray(loanBorrowers[currentPrimaryNumber]?.related_phone_array) &&
                    loanBorrowers[currentPrimaryNumber]?.related_phone_array.length > 0 &&
                    loanBorrowers[currentPrimaryNumber]?.related_phone_array.map(
                      (phone, index) =>
                        loanBorrowers[currentPrimaryNumber]?.related_phone_array[index].relatedPhoneNumber !== null &&
                        loanBorrowers[currentPrimaryNumber]?.related_phone_array[index].relatedPhoneNumber !== '' && (
                          <tr key={index}>
                            <th>Related Phone Numbers:</th>
                            <td>
                              <div>
                                <strong>Phone Number:</strong>
                                <div>{getFormattedPhoneNumber(phone?.relatedPhoneNumber)}</div>
                                <strong>Imported Phone Description:</strong>
                                <div>{phone?.relatedPhoneTag}</div>
                                <strong>Consent to Contact:</strong>
                                <div>{phone?.relatedPhoneConsent ? 'True' : 'False'}</div>
                                <button
                                  className={Styles.deleteButton}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    deleteRelatedPhone(currentPrimaryNumber, phone?.$index);
                                    setShowDeleteConfirmation(true);
                                  }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ),
                    )}
                  {showDeleteConfirmation && (
                    <div className={Styles.payHistoryModalOverlay} onClick={() => setShowDeleteConfirmation(false)}>
                      <div className={Styles.payHistoryModalContent} onClick={(e) => e.stopPropagation()}>
                        <h2 className={Styles.importantText}>Are you sure you want to proceed?</h2>
                        <br />
                        <h3 className={Styles.importantText}>This action cannot be undone once you click "Save".</h3>
                        <h3 className={Styles.importantText}>
                          To undo this action, press "Cancel" at the bottom of the page.
                        </h3>
                        <button
                          type='button'
                          onClick={() => setShowDeleteConfirmation(false)}
                          className={Styles.payHistoryCloseButton}
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </tbody>
            <tr>
              <td colSpan={2} style={{ height: '20px' }}></td>
            </tr>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default ContactInfo;
